import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./App.css";
import Deposit from "./Deposit";
import Withdrawal from "./Withdrawal";
import {
  clearDepositHistory,
  DEPOSIT_HISTORY_CHANGED_EVENT,
  DEPOSIT_HISTORY_STORAGE_KEY,
  loadDepositHistory,
  upsertDepositHistoryEntry
} from "./depositHistory";

const THEME_STORAGE_KEY = "websdk-sandbox-theme";

const getInitialTheme = () => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === "light"
      ? "light"
      : "dark";
  } catch {
    return "dark";
  }
};

const formatHistoryTime = (value) => {
  if (!value) {
    return "Unknown time";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown time";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

const formatHistoryAmount = (entry) => {
  const amount = entry.amount ?? "-";
  return entry.currency ? `${amount} ${entry.currency}` : amount;
};

const getHistoryStatusLabel = (entry) => entry.status || "Pending";

const getHistoryStatusInitial = (entry) =>
  getHistoryStatusLabel(entry).trim().charAt(0).toUpperCase() || "P";

const hasHistoryValue = (value) => {
  if (value == null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "object") {
    return Object.values(value).some(hasHistoryValue);
  }

  return value !== "";
};

const getDefaultHistoryDataTab = (entry) => {
  if (hasHistoryValue(entry?.deposit) || entry?.depositFetchError) {
    return "deposit";
  }

  if (hasHistoryValue(entry?.result)) {
    return "result";
  }

  if (hasHistoryValue(entry?.request || entry?.data)) {
    return "request";
  }

  return "summary";
};

const getHistorySummaryRows = (entry) =>
  [
    ["Status", entry.status || "Pending"],
    ["Amount", formatHistoryAmount(entry)],
    ["Payment Option", entry.paymentOption?.displayName],
    ["Payment Option ID", entry.paymentOption?.id],
    ["Attempt ID", entry.attemptId],
    ["Deposit ID", entry.depositId],
    ["Required Action", entry.requiredAction],
    ["CTA URL", entry.ctaUrl],
    ["Payment Code", entry.cashTransferCode],
    ["Error Code", entry.errorCode],
    ["Error Message", entry.errorMessage],
    ["Deposit Fetch Error", entry.depositFetchError],
    ["Created", formatHistoryTime(entry.createdAt)],
    ["Updated", formatHistoryTime(entry.updatedAt)]
  ].filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );

const getHistoryTabValue = (entry, tabId) => {
  switch (tabId) {
    case "request":
      return entry.request || entry.data;
    case "result":
      return entry.result;
    case "deposit":
      if (entry.deposit) {
        return entry.deposit;
      }

      return entry.depositFetchError
        ? { depositFetchError: entry.depositFetchError }
        : null;
    case "error":
      return entry.error;
    default:
      return null;
  }
};

const getHistoryDepositRefreshId = (entry) =>
  entry?.depositId ||
  entry?.deposit?.depositId ||
  entry?.deposit?.id ||
  entry?.result?.depositId ||
  entry?.result?.deposit?.depositId ||
  entry?.result?.deposit?.id ||
  entry?.result?.id;

const canRefreshHistoryDeposit = (entry) => {
  return (
    Boolean(getHistoryDepositRefreshId(entry)) &&
    `${entry.status || "Pending"}`.toLowerCase().includes("pending")
  );
};

function App() {
  const [isDeposit, setIsDeposit] = useState(true);
  const [theme, setTheme] = useState(getInitialTheme);
  const [themeRoot, setThemeRoot] = useState(null);
  const [historyRoot, setHistoryRoot] = useState(null);
  const [depositHistory, setDepositHistory] = useState(loadDepositHistory);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyDataEntry, setHistoryDataEntry] = useState(null);
  const [historyDataTab, setHistoryDataTab] = useState("summary");
  const [refreshingDepositId, setRefreshingDepositId] = useState(null);

  useEffect(() => {
    setThemeRoot(document.getElementById("theme-toggle-root"));
    setHistoryRoot(document.getElementById("history-root"));
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;

    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // localStorage can be unavailable in stricter browser modes.
    }
  }, [theme]);

  useEffect(() => {
    const refreshDepositHistory = (event) => {
      setDepositHistory(event.detail?.entries || loadDepositHistory());
    };

    const refreshFromStorage = (event) => {
      if (event.key === DEPOSIT_HISTORY_STORAGE_KEY) {
        setDepositHistory(loadDepositHistory());
      }
    };

    window.addEventListener(
      DEPOSIT_HISTORY_CHANGED_EVENT,
      refreshDepositHistory
    );
    window.addEventListener("storage", refreshFromStorage);

    return () => {
      window.removeEventListener(
        DEPOSIT_HISTORY_CHANGED_EVENT,
        refreshDepositHistory
      );
      window.removeEventListener("storage", refreshFromStorage);
    };
  }, []);

  useEffect(() => {
    setHistoryDataEntry((currentEntry) => {
      if (!currentEntry) {
        return currentEntry;
      }

      return (
        depositHistory.find(
          (entry) =>
            (currentEntry.attemptId &&
              entry.attemptId === currentEntry.attemptId) ||
            (currentEntry.depositId &&
              entry.depositId === currentEntry.depositId)
        ) || currentEntry
      );
    });
  }, [depositHistory]);

  useEffect(() => {
    if (!isHistoryOpen && !historyDataEntry) {
      return;
    }

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        if (historyDataEntry) {
          setHistoryDataEntry(null);
        } else {
          setIsHistoryOpen(false);
        }
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [historyDataEntry, isHistoryOpen]);

  useEffect(() => {
    if (!isHistoryOpen && !historyDataEntry) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [historyDataEntry, isHistoryOpen]);

  const handleClearHistory = () => {
    clearDepositHistory();
    setDepositHistory([]);
    setHistoryDataEntry(null);
  };

  const openHistoryDataEntry = (entry) => {
    setHistoryDataEntry(entry);
    setHistoryDataTab(getDefaultHistoryDataTab(entry));
  };

  const refreshHistoryDeposit = async (entry) => {
    const depositRefreshId = getHistoryDepositRefreshId(entry);

    if (!depositRefreshId || refreshingDepositId) {
      return;
    }

    setRefreshingDepositId(depositRefreshId);

    try {
      const deposit = await window.__PGWebSdk.getDepositAsync(depositRefreshId);
      upsertDepositHistoryEntry({
        ...entry,
        depositId: entry.depositId || deposit?.depositId || deposit?.id,
        deposit,
        depositFetchError: null
      });
    } catch (error) {
      upsertDepositHistoryEntry({
        ...entry,
        depositFetchError: error?.message || `${error}`
      });
    } finally {
      setRefreshingDepositId(null);
    }
  };

  const historyDataTabs = historyDataEntry
    ? [
        { id: "summary", label: "Summary", disabled: false },
        {
          id: "request",
          label: "Request",
          disabled: !hasHistoryValue(
            getHistoryTabValue(historyDataEntry, "request")
          )
        },
        {
          id: "result",
          label: "Result",
          disabled: !hasHistoryValue(
            getHistoryTabValue(historyDataEntry, "result")
          )
        },
        {
          id: "deposit",
          label: "Deposit",
          disabled:
            !hasHistoryValue(getHistoryTabValue(historyDataEntry, "deposit")) &&
            !historyDataEntry.depositFetchError
        },
        {
          id: "error",
          label: "Error",
          disabled: !hasHistoryValue(
            getHistoryTabValue(historyDataEntry, "error")
          )
        }
      ]
    : [];
  const selectedHistoryTabValue = historyDataEntry
    ? getHistoryTabValue(historyDataEntry, historyDataTab)
    : null;

  return (
    <>
      {historyRoot &&
        createPortal(
          <div className="history-control">
            <button
              className="history-button"
              type="button"
              aria-haspopup="dialog"
              aria-expanded={isHistoryOpen}
              onClick={() => setIsHistoryOpen((isOpen) => !isOpen)}
            >
              <span>History</span>
              {depositHistory.length > 0 && (
                <span className="history-count">{depositHistory.length}</span>
              )}
            </button>

            {isHistoryOpen && (
              <div
                className="deposit-history-backdrop"
                role="presentation"
                onClick={() => setIsHistoryOpen(false)}
              >
                <section
                  className="deposit-history-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="deposit-history-title"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="deposit-history-header">
                    <div>
                      <h2 id="deposit-history-title">Deposit History</h2>
                      <p>{depositHistory.length} saved attempts</p>
                    </div>
                    <button
                      className="history-close-button"
                      type="button"
                      aria-label="Close deposit history"
                      onClick={() => setIsHistoryOpen(false)}
                    >
                      <span className="close-icon" aria-hidden="true"></span>
                    </button>
                  </div>

                  <div className="deposit-history-list">
                    {depositHistory.length === 0 ? (
                      <div className="deposit-history-empty">
                        No deposit attempts saved yet.
                      </div>
                    ) : (
                      depositHistory.map((entry) => {
                        const historyStatus = getHistoryStatusLabel(entry);

                        return (
                          <article
                            className="deposit-history-row"
                            key={entry.attemptId || entry.depositId}
                          >
                            <span
                              className={`history-status history-status-compact history-status-${historyStatus
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`}
                              title={historyStatus}
                              aria-label={`Status: ${historyStatus}`}
                            >
                              {getHistoryStatusInitial(entry)}
                            </span>
                            <strong className="deposit-history-amount">
                              {formatHistoryAmount(entry)}
                            </strong>
                            <span className="deposit-history-option">
                              {entry.paymentOption?.displayName ||
                                "Unknown option"}
                            </span>
                            <span className="deposit-history-time">
                              {formatHistoryTime(
                                entry.updatedAt || entry.createdAt
                              )}
                            </span>
                            <div className="deposit-history-row-actions">
                              {canRefreshHistoryDeposit(entry) && (
                                <button
                                  className="history-data-button"
                                  type="button"
                                  disabled={
                                    refreshingDepositId ===
                                    getHistoryDepositRefreshId(entry)
                                  }
                                  onClick={() => refreshHistoryDeposit(entry)}
                                >
                                  {refreshingDepositId ===
                                  getHistoryDepositRefreshId(entry)
                                    ? "Refreshing"
                                    : "Refresh deposit"}
                                </button>
                              )}
                              <button
                                className="history-data-button"
                                type="button"
                                onClick={() => openHistoryDataEntry(entry)}
                              >
                                View
                              </button>
                            </div>
                          </article>
                        );
                      })
                    )}
                  </div>

                  <div className="deposit-history-footer">
                    <button
                      className="secondary-action"
                      type="button"
                      onClick={handleClearHistory}
                      disabled={depositHistory.length === 0}
                    >
                      Clear history
                    </button>
                  </div>
                </section>
              </div>
            )}

            {historyDataEntry && (
              <div
                className="deposit-history-backdrop deposit-history-data-backdrop"
                role="presentation"
                onClick={() => setHistoryDataEntry(null)}
              >
                <section
                  className="deposit-history-panel deposit-history-data-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="deposit-history-data-title"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="deposit-history-header">
                    <div>
                      <h2 id="deposit-history-data-title">Deposit Data</h2>
                      <p>
                        {historyDataEntry.depositId ||
                          historyDataEntry.attemptId}
                      </p>
                    </div>
                    <button
                      className="history-close-button"
                      type="button"
                      aria-label="Close deposit data"
                      onClick={() => setHistoryDataEntry(null)}
                    >
                      <span className="close-icon" aria-hidden="true"></span>
                    </button>
                  </div>
                  <div className="deposit-history-tabs">
                    {historyDataTabs.map((tab) => (
                      <button
                        className={`deposit-history-tab ${
                          historyDataTab === tab.id ? "active" : ""
                        }`}
                        type="button"
                        key={tab.id}
                        disabled={tab.disabled}
                        onClick={() => setHistoryDataTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {historyDataTab === "summary" ? (
                    <div className="deposit-history-summary">
                      {getHistorySummaryRows(historyDataEntry).map(
                        ([label, value]) => (
                          <div
                            className="deposit-history-summary-row"
                            key={label}
                          >
                            <span>{label}</span>
                            <strong>{value}</strong>
                          </div>
                        )
                      )}
                    </div>
                  ) : hasHistoryValue(selectedHistoryTabValue) ? (
                    <pre className="deposit-history-json">
                      {JSON.stringify(selectedHistoryTabValue, null, 2)}
                    </pre>
                  ) : (
                    <div className="deposit-history-empty">
                      No data captured for this tab.
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>,
          historyRoot
        )}

      {themeRoot &&
        createPortal(
          <div className="theme-toggle" aria-label="Theme">
            <button
              className={`theme-toggle-button ${
                theme === "dark" ? "active" : ""
              }`}
              type="button"
              onClick={() => setTheme("dark")}
            >
              Dark
            </button>
            <button
              className={`theme-toggle-button ${
                theme === "light" ? "active" : ""
              }`}
              type="button"
              onClick={() => setTheme("light")}
            >
              Light
            </button>
          </div>,
          themeRoot
        )}

      <div className="toggle-wrapper">
        <button
          className={`toggle-label ${!isDeposit ? "" : "active"}`}
          type="button"
          onClick={() => setIsDeposit(true)}
        >
          Deposit
        </button>

        <button
          className={`toggle-label ${isDeposit ? "" : "active"}`}
          type="button"
          onClick={() => setIsDeposit(false)}
        >
          Withdrawal
        </button>
      </div>

      {isDeposit ? <Deposit /> : <Withdrawal />}
    </>
  );
}

export default App;
